import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCartDetail, removeCourseFromCart, resetRemoveCourseFromCart } from "../../State/Cart/Action";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Divider,
  Paper,
  Button,
  IconButton,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CategoryIcon from "@mui/icons-material/Category";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PaymentIcon from "@mui/icons-material/Payment";
import DeleteIcon from "@mui/icons-material/Delete";
import FailureDialog from "../Tools/FailureDialog";
import SuccessDialog from "../Tools/SuccessDialog";
import ConfirmationDialog from "../Tools/ConfirmationDialog";
import {
  createOrder,
  resetCreateOrder,
  resetAllActions,
} from "../../State/Order/Action";
import { useNavigate } from "react-router-dom";
import ProceedToBuyButton from "./ProceedToBuyButton";

const Cart = () => {
  const dispatch = useDispatch();
  const cart = useSelector((store) => store.cart.cartDetail?.data);
  const errorCartDetail = useSelector((store) => store.cart.errorCartDetail);
  const removeCourseFromCartResult = useSelector(
    (store) => store.cart.removeCourseFromCartResult?.data
  );

  const createOrderResult = useSelector(
    (store) => store.order.createOrderResult?.data
  );

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showFailureDialog, setShowFailureDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCartDetail());
    dispatch(resetRemoveCourseFromCart());
    window.scrollTo(0, 0);
  }, [dispatch]);

  useEffect(() => {
    if (removeCourseFromCartResult) {
      if (removeCourseFromCartResult.success) {
        dispatch(getCartDetail());
        setDialogMessage(
          removeCourseFromCartResult.message ||
            "Course removed from cart successfully."
        );
        setShowSuccessDialog(true);
        dispatch(resetRemoveCourseFromCart());
      } else {
        setDialogMessage(
          removeCourseFromCartResult.message ||
            "Failed to remove course from cart."
        );
        setShowFailureDialog(true);
        dispatch(resetRemoveCourseFromCart());
      }
    }
  }, [removeCourseFromCartResult, dispatch]);

  const handleDeleteClick = (courseId) => {
    setCourseToDelete(courseId);
    setConfirmDialogOpen(true);
  };

  const confirmRemoveCourse = () => {
    if (courseToDelete) {
      dispatch(removeCourseFromCart(courseToDelete));
      setCourseToDelete(null);
    }
    setConfirmDialogOpen(false);
  };

  useEffect(() => {
    if (errorCartDetail) {
      setDialogMessage(
        errorCartDetail.message || "Failed to fetch cart details."
      );
    }
  }, [errorCartDetail]);

  useEffect(() => {
    if (cart && cart.id) {
      dispatch(resetAllActions());
    }
  }, [cart, dispatch]);

  const totalOriginalPrice =
    cart?.courses?.reduce((sum, c) => sum + c.price, 0) || 0;
  const totalDiscount =
    cart?.courses?.reduce((sum, c) => sum + c.discountAmount, 0) || 0;
  const totalFinalPrice =
    cart?.courses?.reduce((sum, c) => sum + c.finalPrice, 0) || 0;

  return (
    <Box
      sx={{
        p: 4,
        background: "linear-gradient(to bottom, #f0f4ff, #ffffff)",
        minHeight: "100vh",
        pb: 10,
      }}
    >
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          setDialogMessage("");
        }}
        message={dialogMessage}
      />
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmRemoveCourse}
        title="Remove Course?"
        message="Are you sure you want to remove this course from your cart?"
      />

      <Paper elevation={4} sx={{ p: 3, mb: 4, backgroundColor: "#e3f2fd" }}>
        <Box display="flex" alignItems="center" gap={2}>
          <ShoppingCartIcon fontSize="large" color="primary" />
          <Typography variant="h4" fontWeight="bold">
            Your Learning Cart
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          Hello, <strong>{cart?.learnerName || "Learner"}</strong>! You have{" "}
          <strong>{cart?.totalCourses || 0}</strong> course(s) in your cart.
        </Typography>
      </Paper>
      {dialogMessage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "2vh",
            p: 1,
            m: 1,
            borderRadius: 2,
            background: "linear-gradient(135deg, #ffe0e0, #ffd6d6)",
            boxShadow: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#d32f2f",
              fontWeight: "bold",
              textAlign: "center",
              fontFamily: "'Roboto Slab', serif",
              letterSpacing: 1,
            }}
          >
            {dialogMessage}
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Left side - Courses */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={3}>
            {cart?.courses?.map((course) => (
              <Grid item xs={12} key={course.id}>
                <Card sx={{ display: "flex", borderRadius: 3, boxShadow: 5 }}>
                  <CardMedia
                    component="img"
                    image={course.thumbnailUrl}
                    alt={course.name}
                    sx={{
                      width: 150,
                      height: 150,
                      objectFit: "cover",
                      borderRadius: 2,
                      m: 2,
                    }}
                  />
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      flex: 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Typography variant="h6" fontWeight="bold">
                        <SchoolIcon sx={{ mr: 1 }} fontSize="small" />
                        {course.name}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(course.id)}
                        sx={{ mt: -1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      <AccountCircleIcon sx={{ mr: 1 }} fontSize="small" />
                      {course.instructorName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      <CategoryIcon sx={{ mr: 1 }} fontSize="small" />
                      {course.category}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box
                      display="flex"
                      gap={2}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body1"
                        sx={{ textDecoration: "line-through" }}
                      >
                        ₹{course.price}
                      </Typography>
                      <Typography variant="body1" color="error">
                        -₹{course.discountAmount} Off
                      </Typography>
                      <Typography variant="h6" color="primary">
                        <MonetizationOnIcon sx={{ mr: 0.5 }} fontSize="small" />{" "}
                        ₹{course.finalPrice}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Right side - Summary */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={5}
            sx={{ p: 3, borderRadius: 3, backgroundColor: "#fefefe" }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <AutoAwesomeIcon color="warning" sx={{ mr: 1 }} />
              Order Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Courses ({cart?.totalCourses || 0})</Typography>
              <Typography>₹{totalOriginalPrice.toFixed(2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="error">Discount</Typography>
              <Typography color="error">
                -₹{totalDiscount.toFixed(2)} Off
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Final Price</Typography>
              <Typography variant="h6" color="primary">
                ₹{totalFinalPrice.toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" color="success.main" mb={2}>
              🎉 You are saving ₹{totalDiscount.toFixed(2)} on this order!
            </Typography>
            <ProceedToBuyButton
              cartId={cart?.id}
              disabled={!cart?.totalCourses || cart.totalCourses === 0}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Cart;
